import { Heading, IconButton, LuIcon, Menu, SegmentedTabs } from "@overmap-ai/blocks"
import { type Document, type DocumentAttachment, OrganizationAccessLevel, type Stored } from "@overmap-ai/common"
import { selectAttachmentsOfDocument, selectDocumentById } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import type { ChainedCommands } from "@tiptap/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, type JSONContent, useEditor } from "@tiptap/react"
import { type FocusEventHandler, memo, useCallback } from "react"

import { AttachmentList } from "../../components/AttachmentList/AttachmentList"
import { AttachmentButton } from "../../components/Buttons/AttachmentButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { LuDynamicIcon } from "../../components/Icons/LuDynamicIcon"
import type { IconType } from "../../components/Icons/typings"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { IconColorPickerPopover } from "../../components/Popovers/IconColorPickerPopover"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import {
	BASE_EXTENSIONS,
	DocumentAttachmentNode,
	LINK_NODES,
	MENTION_EXTENSIONS,
	useEditorBlur,
	useEditorContent,
} from "../../tiptap"
import { EditorToolbar, NewLineFloatingMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { DocumentFileHandler } from "../../tiptap/extensions"
import { BaseAttachmentNode } from "../../tiptap/nodes/BaseAttachment/extension"

enum DocumentDetailsTabs {
	VIEW = "view",
	ATTACHMENTS = "attachments",
}

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/documents/$documentId")

export const ProjectDocumentDetailsView = memo(() => {
	const { documentId } = routeApi.useParams()

	const document = useAppSelector(selectDocumentById(documentId))!
	const isAdminUser = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)
	const attachments = useShallowEqaulSelector(selectAttachmentsOfDocument(documentId))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const navigate = useNavigate()

	const handleAttachFiles = useCallback(
		(files: File[]) => {
			return sdk.documentAttachments.bulkAdd(
				files.map((file) => ({
					document: document.uuid,
					file,
				})),
			)
		},
		[document.uuid],
	)

	const handlePatchDocument = useCallback(
		(documentFields: Partial<Omit<Document, "project" | "organization">>) => {
			void sdk.documents.update({ uuid: document.uuid, ...documentFields })
		},
		[document.uuid],
	)

	const handleDocumentTitleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			handlePatchDocument({ title: e.currentTarget.value })
		},
		[handlePatchDocument],
	)

	const handleContentChange = useCallback(
		(JSONContent: JSONContent | undefined) => {
			handlePatchDocument({
				content: JSONContent ? JSON.stringify(JSONContent) : null,
			})
		},
		[handlePatchDocument],
	)

	const handleDocumentIconChange = useCallback(
		(icon: IconType | null) => {
			if (!icon) return
			handlePatchDocument({ icon })
		},
		[handlePatchDocument],
	)

	const handleDocumentColorChange = useCallback(
		(color: string | null) => {
			if (!color) return
			handlePatchDocument({ color: color })
		},
		[handlePatchDocument],
	)

	const handleDeleteDocument = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete document.",
			description: "Are you sure you want to delete this document? This cannot be undone.",
			onAction: async () => {
				await navigate({
					to: "/$organizationId/$projectId/documents",
					from: "/$organizationId/$projectId/documents/$documentId",
				})

				void sdk.documents.delete(document.uuid)
			},
		})
	}, [document.uuid, navigate, openConfirmDeleteDialog])

	const handleDeleteAttachment = useCallback(
		(attachment: Stored<DocumentAttachment>) => {
			openConfirmDeleteDialog({
				title: "Delete attachment",
				description: "The attachment will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.documentAttachments.delete(attachment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	const documentTitleTextAreaProps = useBlurInput<HTMLInputElement>(
		document.title ?? "",
		handleDocumentTitleBlur,
		true,
	)

	const editorContent = useEditorContent(document.content)
	const handleEditorBlur = useEditorBlur(editorContent, handleContentChange)

	const editor = useEditor({
		content: editorContent,
		editable: isAdminUser,
		editorProps: {
			attributes: { class: "outline-none" },
		},
		onBlur: handleEditorBlur,
		extensions: [
			...BASE_EXTENSIONS,
			...MENTION_EXTENSIONS,
			...LINK_NODES,
			DocumentAttachmentNode,
			DocumentFileHandler.configure({
				uuid: document.uuid,
			}),
			Placeholder.configure({
				placeholder: "Enter text",
			}),
		],
	})

	const handleInsertAttachments = useCallback(
		(chainedCommands: () => ChainedCommands, files: File[]) => {
			void handleAttachFiles(files).then(([offlineAttachments]) => {
				chainedCommands()
					.insertContent(
						offlineAttachments.map((offlineAttachment) => ({
							type: BaseAttachmentNode.name,
							attrs: {
								uuid: offlineAttachment.uuid,
							},
						})),
					)
					.run()
			})
		},
		[handleAttachFiles],
	)

	return (
		<ViewContainer maxWidth="6xl">
			<SegmentedTabs.Root
				className="flex size-full flex-col gap-2 overflow-y-hidden"
				defaultValue={DocumentDetailsTabs.VIEW}
			>
				<div className="flex w-full items-center gap-2">
					<div className="flex grow items-center gap-2">
						<IconColorPickerPopover
							className="w-[340px]"
							color={document.color}
							onColorChange={handleDocumentColorChange}
							selectedIcon={document.icon}
							onIconChange={handleDocumentIconChange}
							size="sm"
						>
							<IconButton aria-label="icon" accentColor="base" variant="soft">
								<LuDynamicIcon icon={document.icon as IconType} color={document.color} />
							</IconButton>
						</IconColorPickerPopover>

						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="disable">
							<input
								className="placeholder-(--base-a9) shrink-0 grow resize-none text-xl font-bold outline-none"
								placeholder="Enter a title"
								{...documentTitleTextAreaProps}
							/>
						</OrganizationAccessRequired>
					</div>

					<Menu.Root>
						<Menu.ClickTrigger>
							<IconButton aria-label="more" size="sm" variant="ghost" accentColor="base">
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
								{/*<Menu.Separator />*/}
								<Menu.Item onSelect={handleDeleteDocument}>
									<LuIcon icon={modelIconMapping.delete} />
									Delete
								</Menu.Item>
							</OrganizationAccessRequired>
						</Menu.Content>
					</Menu.Root>
				</div>
				<SegmentedTabs.List variant="surface" size="sm" accentColor="base" className="mb-2">
					<SegmentedTabs.Trigger value={DocumentDetailsTabs.VIEW} className="basis-1">
						View
					</SegmentedTabs.Trigger>
					<SegmentedTabs.Trigger value={DocumentDetailsTabs.ATTACHMENTS} className="basis-1">
						Attachments
					</SegmentedTabs.Trigger>
				</SegmentedTabs.List>

				<SegmentedTabs.Content
					className="flex size-full flex-col gap-4 overflow-hidden"
					value={DocumentDetailsTabs.VIEW}
				>
					<EditorContext value={editor}>
						<EditorToolbar editor={editor} />
						<EditorContent className="w-full grow overflow-y-auto overflow-x-hidden" editor={editor} />
						<NewLineFloatingMenu editor={editor} onAttachFiles={handleInsertAttachments} />
					</EditorContext>
				</SegmentedTabs.Content>

				<SegmentedTabs.Content
					className="w-full grow overflow-y-auto overflow-x-hidden"
					value={DocumentDetailsTabs.ATTACHMENTS}
				>
					<section className="flex h-max w-full flex-col gap-2">
						<div className="flex h-max w-full items-center justify-between">
							<Heading>Attachments</Heading>
							<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
								<AttachmentButton
									icon
									accentColor="base"
									size="sm"
									variant="soft"
									onAttachFiles={handleAttachFiles}
								>
									<LuIcon icon={modelIconMapping.add} />
								</AttachmentButton>
							</OrganizationAccessRequired>
						</div>

						<AttachmentList
							attachments={attachments}
							onDelete={handleDeleteAttachment}
							service={sdk.documentAttachments}
						/>
					</section>
				</SegmentedTabs.Content>
			</SegmentedTabs.Root>
		</ViewContainer>
	)
})
ProjectDocumentDetailsView.displayName = "ProjectDocumentDetailsView"
